import api, { ApiResponse, QueryPayload, QueryResponse, WhereCondition } from './api';
import type { ActivityEntry } from '../components/ActivityCell';

/** Mirrors the server's WorkingHours: minutes from midnight, never crossing midnight. */
export interface WorkingWindow {
  days: number[];
  start: number;
  end: number;
}

export interface WorkingHours {
  windows: WorkingWindow[];
}

export interface Network {
  activity_status?: ActivityEntry | null;
  id: string;
  seq?: number;
  ref?: string;
  postback_url?: string; // present on getById only
  workspace_id: string;
  tag_ids?: string[];
  name: string;
  kind?: string | null;
  notes?: string | null;
  report_to_source: boolean;
  use_redirect_page?: boolean;
  referrer_policy?: 'none' | 'origin' | 'full';
  click_id_param: string;
  currency_code: string;
  working_hours?: WorkingHours | null;
  /** Fixed UTC offset (-12..14) shared by the caps and working hours. */
  timezone?: number;
  /** Daily ceilings, whole units, 0 = no cap. Spend/revenue in the network currency. */
  daily_spend_cap?: number;
  daily_conversion_cap?: number;
  daily_revenue_cap?: number;
  created_at: string;
  updated_at: string;
}

export const networksService = {
  query: async (filters: { limit?: number; offset?: number; search?: string; where?: WhereCondition ; order_by?: string; order?: 'asc' | 'desc' } = {}): Promise<{ data: Network[]; total: number }> => {
    const where: WhereCondition | undefined = filters.where ?? (filters.search
      ? { network: { name: { op: 'ilike', value: `%${filters.search}%` } } } : undefined);
    const payload: QueryPayload = {
      offset: filters.offset ?? 0,
      limit: filters.limit ?? 100,
      order_by: filters.order_by ?? 'network.created_at',
      order: filters.order ?? 'desc',
      ...(where && { where }),
    };
    const response = await api.post<QueryResponse<Network>>('/networks/query', payload);
    return { data: response.data.data, total: response.data._meta?.total ?? 0 };
  },

  getById: async (id: string): Promise<Network> => {
    const response = await api.get<ApiResponse<Network>>(`/networks/${id}`);
    return response.data.data!;
  },

  getKinds: async (): Promise<string[]> => {
    const response = await api.get<ApiResponse<string[]>>('/networks/kinds');
    return response.data.data ?? [];
  },

  create: async (data: Partial<Network>): Promise<Network> => {
    const response = await api.post<ApiResponse<Network>>('/networks', data);
    return response.data.data!;
  },

  update: async (id: string, data: Partial<Network>): Promise<Network> => {
    const response = await api.patch<ApiResponse<Network>>(`/networks/${id}`, data);
    return response.data.data!;
  },

  delete: async (id: string): Promise<void> => {
    await api.delete(`/networks/${id}`);
  },

  unarchive: async (id: string): Promise<Network> => {
    const response = await api.patch<ApiResponse<Network>>(`/networks/${id}/unarchive`);
    return response.data.data!;
  },
};
